import { apiService } from '../../../services/apiService';
import { API_PATHS } from '../../../utils/apiPaths';
import { extractApiData, isApiSuccess } from '../../../utils/helpers';
import type { PlatformUserListResponse, PlatformUserUpdateRequest } from '../../user-management/models/types';
import type { AuthUserSnapshot } from '../models/types';
import {
  buildFallbackProfile,
  mapProfileDto,
  normalizeProfileUsers,
} from '../utils/utils';

export async function fetchProfileForUser(authUser: AuthUserSnapshot) {
  if (authUser.userType === 'MERCHANT') {
    return buildFallbackProfile(authUser);
  }

  const response: unknown = await apiService.post(API_PATHS.GET_PLATFORM_USERS, {
    search: authUser.email,
  });

  if (!isApiSuccess(response)) {
    throw new Error((response as { message?: string })?.message || 'Unable to load profile.');
  }

  const rows = normalizeProfileUsers(extractApiData<PlatformUserListResponse>(response));
  const match =
    rows.find((row) => Number(row.userId) === authUser.id) ??
    rows.find((row) => String(row.email ?? '').toLowerCase() === String(authUser.email ?? '').toLowerCase());

  if (match) {
    return mapProfileDto(match, {
      ...authUser,
      name: authUser.fullName?.trim() || '—',
      email: authUser.email?.trim() || '—',
      phone: authUser.phone?.trim() || '—',
      role: authUser.adminType || authUser.role || 'admin',
      status: authUser.status || 'ACTIVE',
      userType: authUser.userType ?? 'ADMIN',
    });
  }

  return buildFallbackProfile(authUser);
}

export async function updatePlatformUser(payload: PlatformUserUpdateRequest) {
  const response: unknown = await apiService.post(API_PATHS.UPDATE_PLATFORM_USER, payload);
  if (!isApiSuccess(response)) {
    throw new Error((response as { message?: string })?.message || 'Unable to update profile.');
  }
  return response;
}
